import { el, clear } from "../components/dom.js";
import {
	getAnimeById,
	setSeasonRating,
	setSeasonSeen,
	setEpisodeRating,
	setEpisodeSeen,
	getCatalog,
	saveCatalog,
	effectiveRating,
	effectiveSeen,
	hasOwnRating,
	absoluteEpisodeNumbers,
	epKey,
} from "../db/anime.js";
import { getAnimeEpisodes } from "../api/anime.js";
import { formatDate, formatReleaseDate, todayDate } from "../utils/dates.js";
import {
	seasonReleased,
	seasonPremiereDate,
	isCatalogStale,
} from "../utils/seasons.js";
import { navigate, navigateBack } from "../main.js";
import { magScreenHead, magEpisodeRow, MAG } from "../components/magazine/kit.js";
import { magField, magRating } from "../components/magazine/detail.js";

export async function renderAnimeSeasonDetail(root, _match, id, seasonStr) {
	const seasonNum = Number(seasonStr);
	const anime = await getAnimeById(id);
	if (!anime) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Anime not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/anime") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);

	let catalog = await getCatalog(anime.tvmaze_id);
	if ((!catalog || isCatalogStale(catalog)) && anime.tvmaze_id) {
		try {
			catalog = await saveCatalog(await getAnimeEpisodes(anime.tvmaze_id));
		} catch {
			// Keep whatever we had on fetch failure.
		}
	}
	mount(view, id, seasonNum, await getAnimeById(id), catalog);
}

function mount(view, id, seasonNum, anime, catalog) {
	// Keep scroll position across the re-mount that follows an episode rating/seen
	// toggle, instead of jumping back to the top of the season.
	const scrollY = window.scrollY;
	clear(view);

	const header = magScreenHead(`Season ${seasonNum}`, {
		eyebrow: anime.title,
		eyebrowColor: MAG.lime,
		onBack: () => navigateBack(`/anime/${id}`),
	});
	view.append(header);

	// A not-yet-aired season doesn't inherit the entry's watched/rating defaults
	// (an explicit per-season override still wins).
	const today = todayDate();
	const seasonObj =
		(catalog?.seasons ?? []).find((s) => s.number === seasonNum) ?? {
			number: seasonNum,
		};
	const released = seasonReleased(seasonObj, catalog?.episodes ?? [], today);

	// Season-level controls.
	const seasonRating = magField(
		"Season rating",
		magRating(effectiveRating(anime, seasonNum, null, { released }), async (value) => {
			mount(view, id, seasonNum, await setSeasonRating(id, seasonNum, value), catalog);
		}),
		{ index: 0 },
	);

	const seasonSeen = effectiveSeen(anime, seasonNum, null, { released });
	const seenBtn = magField(
		"Whole season",
		el(
			"button",
			{
				type: "button",
				onclick: async () => {
					mount(view, id, seasonNum, await setSeasonSeen(id, seasonNum, !seasonSeen), catalog);
				},
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "11px",
					letterSpacing: ".06em", textTransform: "uppercase", borderRadius: "999px",
					padding: "11px 16px", cursor: "pointer", lineHeight: "1",
					color: seasonSeen ? MAG.lime : "#0b0b0f",
					background: seasonSeen ? "transparent" : MAG.lime,
					border: seasonSeen ? `1.5px solid ${MAG.lime}` : "0",
				},
			},
			seasonSeen ? "✓ Seen" : "Mark season seen",
		),
		{ index: 1 },
	);

	view.append(seasonRating, seenBtn);

	if (!released) {
		const d = seasonPremiereDate(seasonObj, catalog?.episodes ?? []);
		view.append(
			el("p", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12px",
					color: MAG.dim, padding: "2px 16px 0",
				},
			}, d ? `Hasn't aired yet — airs ${formatReleaseDate(d)}.` : "Hasn't aired yet."),
		);
	}

	// Episodes. Display uses Crunchyroll-style continuous numbering across the
	// whole series; storage keys stay per-season (season × number).
	const absByKey = absoluteEpisodeNumbers(catalog?.episodes);
	const eps = (catalog?.episodes ?? [])
		.filter((e) => e.season === seasonNum && e.number != null)
		.sort((a, b) => a.number - b.number);

	if (!catalog) {
		view.append(el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12px",
				color: MAG.dim, padding: "12px 16px",
			},
		}, "Loading episodes…"));
		return;
	}
	if (eps.length === 0) {
		view.append(el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12px",
				color: MAG.dim, padding: "12px 16px",
			},
		}, "No episodes found."));
		return;
	}

	const list = el("div", {
		style: { display: "flex", flexDirection: "column", gap: "9px", padding: "0 16px" },
	});
	for (const ep of eps) {
		const epReleased = ep.airdate ? ep.airdate <= today : released;
		list.append(episodeRow(view, id, seasonNum, anime, catalog, ep, absByKey, epReleased));
	}
	view.append(list);
	window.scrollTo(0, scrollY);
}

// One episode: the magEpisodeRow (numbered thumb + title + the seen toggle mapped
// onto `owned`/"Seen"), with the per-episode rating editor stacked beneath it.
function episodeRow(view, id, seasonNum, anime, catalog, ep, absByKey, released = true) {
	const displayNum = absByKey.get(epKey(seasonNum, ep.number)) ?? ep.number;
	const seen = effectiveSeen(anime, seasonNum, ep.number, { released });

	// Seen state drives the trailing control: ✓ Seen (lime outline) vs Mark seen
	// (solid lime), tapping it toggles seen — the "owned" mapping the kit expects.
	const row = magEpisodeRow(
		{ ...anime, title: ep.name || "Untitled" },
		{
			number: displayNum,
			kicker: ep.airdate ? formatDate(ep.airdate) : null,
			owned: seen,
			ownedLabel: "Seen",
			addLabel: "Mark seen",
			onAdd: async () => {
				mount(view, id, seasonNum, await setEpisodeSeen(id, seasonNum, ep.number, !seen), catalog);
			},
		},
	);

	const owns = hasOwnRating(anime, seasonNum, ep.number);
	const ratingWrap = el(
		"div",
		{ style: { display: "flex", alignItems: "center", gap: "10px", padding: "2px 12px 0" } },
		magRating(effectiveRating(anime, seasonNum, ep.number, { released }), async (value) => {
			mount(view, id, seasonNum, await setEpisodeRating(id, seasonNum, ep.number, value), catalog);
		}),
		owns
			? el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
					letterSpacing: ".06em", textTransform: "uppercase", color: MAG.lime,
					border: "1px solid rgba(182,242,61,.5)", borderRadius: "6px", padding: "3px 7px",
				},
			}, "set")
			: null,
	);

	return el(
		"div",
		{ style: { display: "flex", flexDirection: "column", gap: "4px" } },
		row,
		ratingWrap,
	);
}
